import {Injectable} from '@angular/core';
import {
	AuthApiService, CompanyDocumentModel,
	CompanyEmployeeModel,
	CompanyEmployeesService, FundInvestmentReportInterface,
	HttpStatusCodesEnum,
	IndividualExtendedModel,
	IndividualsService,
	UserRelationTypesEnum
} from "@spa/api";
import {IndividualIdentityDetailModel} from "@spa/api/lib/models/individual-identity-detail";
import {IndividualDocumentModel} from "@spa/api/lib/models/individual-document";
import {Observable, ReplaySubject} from "rxjs";
import {of} from "rxjs/internal/observable/of";
import {map} from "rxjs/operators";
import {AlertsService} from "./alerts.service";
import {Helper} from "../classes";

@Injectable({
	providedIn: 'root'
})
export class ClientsService {
	public currentClient: ReplaySubject<Client>;

	constructor(
		private alertsService: AlertsService,
		private authApiService: AuthApiService,
		private individualsService: IndividualsService,
		private employeesService: CompanyEmployeesService) {

		this.currentClient = new ReplaySubject<Client>();
	}

	public refresh(): void {
		const subscription = this.get().subscribe(client => {
			this.currentClient.next(client);
			subscription.unsubscribe();
		})

	}

	public get(): Observable<Client> {
		const authUser = this.authApiService.getAuthenticatedUser();

		console.log('auth 2fa', authUser.twoFactorEnabled)

		if (authUser.userRelationType === UserRelationTypesEnum.Individual) {
			return this.individualsService.getSelfExtended(false)
				.pipe(map(response => {
					if (response.status !== HttpStatusCodesEnum.Ok) {
						this.alertsService.addAlert('Error while fetching individual client.');

						return null;
					}

					const individual = response.data as IndividualExtendedModel;

					return Client.parseIndividual(individual, authUser.twoFactorEnabled);
				}));
		} else if (authUser.userRelationType === UserRelationTypesEnum.CompanyEmployee) {
			return this.employeesService.getSelfCompanyEmployee(true)
				.pipe(map(response => {
					if (response.status !== HttpStatusCodesEnum.Ok) {
						this.alertsService.addAlert('Error while fetching company employee client.');

						return null;
					}

					const employee = response.data as CompanyEmployeeModel;

					return Client.parseEmployee(employee, authUser.twoFactorEnabled);
				}));
		}

		this.alertsService.addAlert('User type is not allowed in this portal.');

		return of(null);
	}

	public getCurrent(): Client {
		const authUser = this.authApiService.getAuthenticatedUser();
		console.log('auth 2fa', authUser.twoFactorEnabled)

		if (Helper.isPrototypePortal() && !authUser)
		{
			return null;
		}

		if (authUser.userRelationType === UserRelationTypesEnum.Individual) {
			const individual = this.individualsService.getCurrentIndividual()

			return Client.parseIndividual(individual, authUser.twoFactorEnabled);
		} else if (authUser.userRelationType === UserRelationTypesEnum.CompanyEmployee) {
			const employee = this.employeesService.getCurrentEmployee(true) as CompanyEmployeeModel;

			return Client.parseEmployee(employee, authUser.twoFactorEnabled);
		}

		this.alertsService.addAlert('User type is not allowed in this portal.');

		return null;
	}
}

export class Client {
	static parseEmployee(employee: CompanyEmployeeModel, twoFaEnabled: boolean): Client {
		const client = new Client();

		client.userRelationType = UserRelationTypesEnum.CompanyEmployee;
		client.id = employee.id;
		client.companyId = employee.companyId;
		client.email = employee.email;
		client.userCorrelationId = employee.userId;
		client.phoneNumber = employee.phoneNumber;
		client.name = employee.name;
		client.twoFactorEnabled = twoFaEnabled;

		return client
	}

	static parseIndividual(individual: IndividualExtendedModel, twoFaEnabled: boolean): Client {
		const client = new Client();

		client.userRelationType = UserRelationTypesEnum.Individual;
		client.id = individual.id;
		client.email = individual.email;
		client.documents = individual.documents;
		client.userCorrelationId = individual.userCorrelationId;
		client.phoneNumber = individual.phoneNumber;
		client.identityDetails = individual.identityDetails;
		client.initialDepositId = individual.initialDepositId;
		client.name = individual.name;
		client.twoFactorEnabled = twoFaEnabled;

		return client
	}

	userRelationType: UserRelationTypesEnum;
	id: number;
	companyId: number;
	email: string;
	userCorrelationId: number;
	phoneNumber: string;
	name: string;
	documents: (IndividualDocumentModel | CompanyDocumentModel)[];
	identityDetails: IndividualIdentityDetailModel[];
	initialDepositId: number;
	twoFactorEnabled: boolean;

	get isCorporate(): boolean {
		return this.userRelationType === UserRelationTypesEnum.CompanyEmployee;
	}

	get customerId(): number {
		return this.isCorporate ? this.companyId : this.id;
	}

	get extendedId() : string {
		const maxChars = 6;
		const extendedId = this.id.toString().padStart(maxChars, '0');

		if (this.isCorporate) {
			return `C${extendedId}`;
		}

		return `I${extendedId}`;
	}
}
